import { faTasks, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@mui/material';
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
} from 'reactstrap';
import Select from 'react-select';
import * as moment from 'moment';
import CommonContext, { ApiRoutes } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    IconCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    VariableLinkCellRenderer,
    FlagIconCellRenderer,
    TimesheetNumberCellRenderer,
} from '../common/dataGrid/DataGrid';
import { CompactSelectStyles } from '../common/forms/ValidatedSelect';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    FlexCenterRow,
    onFieldChange,
    PageHeading,
    PageWrap,
    toasty,
} from '../common/forms/FormElements';
import { util } from '../Util';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { NotAuthorizedPage } from '../status/StatusCodes';
import PayrollCalculationSlideout from '../payrollManagement/PayrollCalculationSlideout';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { PayrollFlags } from './Payroll';

class PayrollIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();
        this.payrollCalculationRef = React.createRef();

        // Synchronous guard flag for the Reject button (double-click protection)
        this.isRejecting = false;

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            selectedPayrollCompany: null,
            selectedDispatchLocation: null,
            selectedMultiDispatchLocations: null,
            isRejectingPayrollTimesheet: false,
            showRejectPayrollTimesheetModal: false,
            selectedWeekOf: '',
            selectEmployee: 0,
            showExportModal: false,
            isExporting: false,
            isDownloading: false,
            isRecalculating: false,
            showDownloadModal: false,
            showRecalcModal: false,
            selectedExportId: null,
            selectedRowRejectionNotes: '',
            showProcessModal: false,
            isProcessing: false,
            // Reject modal new fields
            rejectSelectedEmployee: null,
            rejectDestination: 'ticket', // 'ticket' | 'payroll'
            rejectMode: 'day', // 'day' or 'week'
            rejectDate: '',
            rejectEmployees: [],
            rejectPayrollStatus: 'all', // 'all' | 'pending' | 'processed' (only used when rejectMode === 'week')
            rejectDispatchLocation: null,
            isLoadingRejectEmployees: false,
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() { return this.populateState(); }

    componentWillUnmount() {
        return this.setState = (state, callback) => {

        };
    }

    async onAcceptButtonClick() {
        const { rowsSelected } = this.state;

        // call accept
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id);
            const response = await util.fetch.post(
                ApiRoutes.payroll.approve(),
                model,
            );

            if (response) {
                toasty.error(`Error approving payroll records: ${response}`);
            } else {
                toasty.success('Payroll records approved');
            }
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [] });
    }

    onChange = onFieldChange;

    onDownloadExportClick = async () => {
        const { selectedExportId } = this.state;
        this.setState({ isDownloading: true });
        if (selectedExportId) {
            /* var model = { id: id }; */
            await util.fetch
                .downloadFile(
                    ApiRoutes.payroll.download(selectedExportId),
                    null,
                    'Export',
                )
                .catch(this.handleSaveError);
        }
        this.setState({
            selectedExportId: null,
            isDownloading: false,
            showDownloadModal: false,
        });
    };

    onDownloadWeeklyClick = async () => {
        const { selectedExportId } = this.state;
        this.setState({ isDownloading: true });
        if (selectedExportId) {
            /* var model = { id: id }; */
            await util.fetch
                .downloadFile(
                    ApiRoutes.payroll.downloadWeekly(selectedExportId),
                    null,
                    'WeeklyEmployee.xlsx',
                )
                .catch(this.handleSaveError);
        }
        this.setState({
            selectedExportId: null,
            isDownloading: false,
            showDownloadModal: false,
        });
    };

    async onExportClick() {
        const {
            selectedWeekOf,
            selectedMultiDispatchLocations,
        } = this.state;

        if (!selectedWeekOf || !selectedMultiDispatchLocations) return;

        this.setState({ isExporting: true });

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationIds: selectedMultiDispatchLocations.map(
                (e) => e.id,
            ),
        };

        try {
            const response = await util.fetch.post(
                ApiRoutes.payroll.export(),
                model,
            );
            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Export complete');
            }
        } catch {
            toasty.error('Error exporting payroll records');
        }

        this.state.gridOptions.refresh();

        this.setState({
            rowsSelected: [],
            isExporting: false,
            showExportModal: false,
        });
    }

    async onHourlyExportClick() {
        const { exporting } = this.state;

        if (!exporting) {
            const { selectedWeekOf } = this.state;

            if (!selectedWeekOf) return;

            this.setState({ isExporting: true });

            const model = {
                weekOf: new Date(selectedWeekOf),
            };

            try {
                const fileName = `HourlyReport_${moment().format(
                    'YYYY-M-D_hhmmss',
                )}.xlsx`;

                await util.fetch.downloadFile(
                    ApiRoutes.payroll.excelHoursExport(),
                    model,
                    fileName,
                );
            } catch (error) {
                toasty.error('There was a problem getting the hourly export.');
                console.error({ error });
            } finally {
                await this.setState({
                    isExporting: false,
                    showExportModal: false,
                });
            }
        }
    }

    onPayrollCalculationClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    async onRecalcClick() {
        const { selectedWeekOf, selectedDispatchLocation, selectedEmployee } = this.state;

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationId: selectedDispatchLocation,
            employeeId: selectedEmployee,
        };

        // Check to see if this employee has payroll edits
        // If so warn the user of overwrite
        try {
            const hasEdits = await util.fetch.post(
                ApiRoutes.payroll.doesPayrollHaveEdits(),
                model,
            );
            if (hasEdits) {
                const confirmed = window.confirm(
                    'Some of these charges have edits.  Continue and overwrite the edits?',
                );
                if (!confirmed) return;
            }

            const hasExports = await util.fetch.post(
                ApiRoutes.payroll.doesPayrollHaveExports(),
                model,
            );
            if (hasExports) {
                window.alert(
                    'Employees with exported charges cannot be recalculated.',
                );
                return;
            }
        } catch (error) {
            toasty.error('An error occurred while preparing to recalculate.');
            console.error({ error });
            return;
        }

        // Recalculate
        this.setState({ isRecalculating: true });

        try {
            await util.fetch.post(
                ApiRoutes.payrollManagement.recalculateWeek(),
                model,
            );
            this.setState({ isRecalculating: false, showRecalcModal: false });
            toasty.success('Employee\'s week recalculated successfully.');
        } catch (error) {
            toasty.error('An error occurred recalculating.');
            console.error({ error });
        } finally {
            this.setState({ isRecalculating: false });
        }
    }

    async onRejectButtonClick() {
        toasty.error('Not Implemented');
    }

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onSelectedDispatchLocationChanged = async (selection) => {
        const loc = { ...selection };
        this.setState({ selectedDispatchLocation: loc.value });
    };

    onSelectedMultiDispatchLocationChanged = async (opts) => {
        this.setState({ selectedMultiDispatchLocations: opts });
    };

    onSelectedPayrollCompanyChanged = async (selection) => {
        const loc = { ...selection };
        this.setState({ selectedPayrollCompany: loc.value });

        const subArr = this.state.dispatchLocations.filter(
            (x) => x.parentCompanyId == loc.value,
        );
        this.setState({ selectedMultiDispatchLocations: subArr });
    };

    onSelectedPayrollDispatchLocationChanged = async (selection) => {
        const { selectedWeekOf } = this.state;

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationId: selection.id,
        };

        this.setState({
            selectedDispatchLocation: selection.value,
            selectedEmployee: 0,
        });

        let employees = [];
        try {
            const entities = await util.fetch.post(
                ApiRoutes.payroll.employees(),
                model,
            );
            employees = entities.map((e) => ({
                ...e,
                value: e.id,
                label: e.name,
            }));
        } catch (error) {
            console.log(error);
        }

        this.setState({ employees });
    };

    onSelectedPayrollEmployeeChanged = async (selection) => {
        this.setState({ selectedEmployee: selection.value });
    };

    onSelectedPayrollWeekChanged = async (selection) => {
        const { selectedDispatchLocation } = this.state;

        const model = {
            weekOf: selection.currentTarget.value,
            dispatchLocationId: selectedDispatchLocation,
        };

        this.setState({
            selectedWeekOf: selection.currentTarget.value,
            selectedEmployee: 0,
        });

        let employees = [];
        try {
            const entities = await util.fetch.post(
                ApiRoutes.payroll.employees(),
                model,
            );
            if (entities) {
                employees = entities.map((e) => ({
                    ...e,
                    value: e.id,
                    label: e.name,
                }));
            }
        } catch (error) {
            console.log(error);
        }

        this.setState({ employees });
    };

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
    };

    async onWeeklyExportClick() {
        const { exporting } = this.state;

        if (!exporting) {
            const {
                selectedWeekOf,
                selectedDispatchLocation,
                exportBillableOnly,
            } = this.state;

            if (!selectedWeekOf || !selectedDispatchLocation) return;

            this.setState({ isExporting: true });

            const dispatchLocationIds = [];
            dispatchLocationIds.push(selectedDispatchLocation);
            const model = {
                weekOf: new Date(selectedWeekOf),
                dispatchLocationIds,
                exportBillableOnly,
            };

            try {
                let fileName = `PayrollWeeklyExport_${moment().format(
                    'YYYY-M-D_hhmmss',
                )}.xlsx`;

                if (exportBillableOnly) {
                    fileName = `BillingWeeklyExport_${moment().format(
                        'YYYY-M-D_hhmmss',
                    )}.xlsx`;
                }

                await util.fetch.downloadFile(
                    ApiRoutes.payroll.excelWeeklyExport(),
                    model,
                    fileName,
                );
            } catch (error) {
                toasty.error('There was a problem getting the weekly export.');
                console.error({ error });
            } finally {
                await this.setState({
                    isExporting: false,
                    showExportModal: false,
                });
            }
        }
    }

    getColumnDefs(hasFullView, hasEdit, hasIndividual) {

        hasEdit = true;
        hasIndividual = true;
        hasFullView = true;

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: this.state.dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const payrollStatusFilterParams = {
            suppressFilterButton: true,
            options: this.state.payrollStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                minWidth: 35,
                cellRenderer: this.indexCellRenderer,
            },
            {
                colId: 'Payroll.TimesheetDetail.Timesheet.JobDate',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                maxWidth: 120,
                minWidth: 120,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                sortable: false,
                maxWidth: 140,
                minWidth: 140,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'End',
                headerName: 'End',
                field: 'end',
                sortable: false,
                maxWidth: 140,
                minWidth: 140,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Payroll.Employee.FullName',
                headerName: 'Emp. Name',
                field: 'employeeName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellStyle: (q) => {
                    if ((q.data ?? {}).onBoarding) {
                        if (q.data.onBoarding) {
                            return { color: 'red', fontWeight: 'bold' };
                        }
                    }

                    return {};
                },
            },
        ];

        defs.push({
            colId: 'Payroll.DispatchLocationName',
            headerName: 'Dispatching',
            sortable: true,
            flex: 1.5,
            field: 'dispatchLocation',
            tooltipField: 'dispatchOfficeName',
            filter: 'selectFilter',
            filterParams: dispatchFilterParams,
            floatingFilterComponent: 'selectFloatingFilter',
            floatingFilterComponentParams: dispatchFilterParams,
        });
        

        defs.push(
            ...[
                {
                    colId: 'Payroll.CustomerCompanyName',
                    headerName: 'Customer Name',
                    field: 'customerName',
                    sortable: true,
                    flex: 1.5,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    valueGetter: (row) => {
                        if (row.data) {
                            const name = `${row.data.customerName}${row.data.subcontractorName
                                ? ` - ${row.data.subcontractorName}`
                                : ''
                            }`;
                            return name;
                        }

                        return '';
                    },
                },
                {
                    colId: 'Payroll.ChargeTypeName',
                    headerName: 'Charge Type',
                    field: 'chargeType',
                    sortable: true,
                    flex: 1,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'Payroll.TimesheetDetail.Timesheet.TimesheetNumber',
                    headerName: 'Order / Haul #',
                    field: 'jobNumber',
                    sortable: true,
                    flex: 1,
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'timesheetNumberRenderer',
                },
            ],
        );

        defs.push(
            ...[
                {
                    colId: 'Charge',
                    headerName: 'Charge',
                    field: 'charge',
                    type: 'rightAligned',
                    sortable: false,
                    maxWidth: 120,
                    minWidth: 120,
                    cellStyle: (q) => {
                        if ((q.data ?? {}).isBreak) {
                            if (q.data.isPaidBreak) {
                                return { color: 'blue' };
                            }
                            return { color: 'red' };
                        }

                        return {}; // dont return '' will break grid
                    },
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
            ],
        );
        

        defs.push(
            ...[
                {
                    colId: 'Pay',
                    headerName: 'Pay',
                    field: 'pay',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'REG',
                    headerName: 'REG',
                    field: 'reg',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                
                {
                    colId: 'OT',
                    headerName: 'OT',
                    field: 'ot',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
               
                {
                    colId: 'PW',
                    headerName: 'PW',
                    field: 'pw',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'PWOT',
                    headerName: 'PWOT',
                    field: 'pwot',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'VAC',
                    headerName: 'Other',
                    field: 'vac',
                    sortable: false,
                    maxWidth: 100,
                    minWidth: 100,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
            ],
        );

        if (hasEdit) {
            // Users without full access can only access Exported records (although they can only see Print view records.)
            defs.push(
                ...[
                    {
                        colId: 'Payroll.PayrollStatus.Description',
                        headerName: 'Status',
                        field: 'status',
                        sortable: true,
                        maxWidth: 140,
                        minWidth: 140,
                        cellRenderer: 'variableLinkRenderer',
                        cellRendererParams: (r) => {
                            if ((r.data ?? {}).payrollExportId) {
                                return {
                                    clicked: this.toggleDownloadModal,
                                    isLink: true,
                                    nameField: 'status',
                                    idField: 'payrollExportId',
                                    title: 'Download Export',
                                };
                            }
                            return {
                                clicked: this.toggleDownloadModal,
                                isLink: false,
                                nameField: 'status',
                                idField: 'payrollExportId',
                                title: 'Download Export',
                            };
                        },
                        filter: 'selectFilter',
                        floatingFilter: true,
                        filterParams: payrollStatusFilterParams,
                        floatingFilterComponent: 'selectFloatingFilter',
                        floatingFilterComponentParams:
                            payrollStatusFilterParams,
                    },
                    {
                        colId: 'Payroll.PayrollExportId',
                        headerName: 'Export Id',
                        field: 'payrollExportId',
                        sortable: true,
                        maxWidth: 100,
                        minWidth: 100,
                        filter: 'agTextColumnFilter',
                        filterParams: TextFilterDefaults,
                        floatingFilterComponentParams: {
                            suppressFilterButton: true,
                        },
                    },
                ],
            );
        }


        if (hasEdit) {
            defs.push({
                colId: 'Id',
                flex: 0,
                minWidth: 75,
                maxWidth: 75,
                headerName: 'View',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openTimesheetDetails,
                    idField: 'timesheetId',
                    iconClass: 'fa-eye',
                },
            });
        }

        defs.push({
            colId: 'Id',
            minWidth: 75,
            maxWidth: 75,
            headerName: 'Print',
            sortable: false,
            cellRenderer: 'iconRenderer',
            cellRendererParams: {
                clicked: (id) => this.printTimesheet(id, hasFullView),
                idField: 'timesheetId',
                iconClass: 'fa-print',
            },
        });

        if (hasEdit) {
            defs.push({
                colId: 'Id',
                minWidth: 75,
                maxWidth: 75,
                headerName: '',
                sortable: false,
                cellRenderer: 'iconRenderer',
                cellRendererParams: {
                    clicked: this.openPayrollCalculation,
                    idField: 'id',
                    iconClass: 'fa-edit',
                },
            });
        }

        return defs;
    }

    getDispatchOption = async (item) => {
        const ret = { value: item.id, label: item.companyName.trim() };

        return ret;
    };

    getExcelAPIPath(hasFull) {
        const url = hasFull
            ? ApiRoutes.payroll.excelExport()
            : ApiRoutes.payroll.excelExportIndividual();
        return url;
    }

    openPayrollCalculation = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.payrollCalculationRef.current.open(id);
        }
    };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const payrollStatusesPromise = util.fetch.js(
            ApiRoutes.typeAheads.payrollStatuses(),
        );
        const billingStatusesPromise = util.fetch.js(
            ApiRoutes.typeAheads.billingStatuses(),
        );
        const payrollCompaniesPromise = util.fetch.js(
            ApiRoutes.typeAheads.payrollCompanies(),
        );
        const payrollFlagsPromise = util.fetch.js(
            ApiRoutes.typeAheads.payrollCategoryFlags(),
        );
        const userProfilePromise = getTenantUserProfile();

        const [
            payrollStatuses,
            billingStatuses,
            payrollCompanies,
            userProfile,
            flags,
        ] = await Promise.all([
            payrollStatusesPromise,
            billingStatusesPromise,
            payrollCompaniesPromise,
            userProfilePromise,
            payrollFlagsPromise,
        ]);

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        // 2022-10-10 - M. Nicol
        // If the user has Payroll View, (s)he can see all data on the page.
        // See permission setup in App.js.
        // Edit allows use of red Export button.
        // If the user is neither of the below, (s)he must have payroll.viewindividual.
        const canViewBillingReport = !!userProfile.userPermissions.includes('billingreport.view');
        let hasFullView = !!userProfile.userPermissions.includes('payroll.view');
        let hasIndividual = !!userProfile.userPermissions.includes(
            'payroll.viewindividual',
        );
        let hasEdit = !!userProfile.userPermissions.includes('payroll.edit');

        if (this.context?.user?.isAdmin) {
            hasIndividual = false;
            hasEdit = true;
            hasFullView = true;
        }

        let dispatchLocations = await util.fetch.js(
            ApiRoutes.company.dispatchCompanies(),
        );
        dispatchLocations = dispatchLocations.map((e) => ({
            ...e,
            value: e.id,
            label: e.companyName,
        }));

        const self = this;
        const employees = [];
        await this.setState({
            employees,
            dispatchLocations,
            payrollStatuses,
            billingStatuses,
            flags,
            yesNo,
            payrollCompanies,
            hasFullView,
            hasEdit,
            hasIndividual,
        }, function () {
            const gridOptions = createGridOptions(self);

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                iconRenderer: IconCellRenderer,
                flagIconRenderer: FlagIconCellRenderer,
                variableLinkRenderer: VariableLinkCellRenderer,
                timesheetNumberRenderer: TimesheetNumberCellRenderer,
            };

            gridOptions.rowClassRules = {
                'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
                'ag-row-success': (params) => {
                    const matches = ((params.data ?? {}).flag ?? []).filter((f) => (
                        f === PayrollFlags.ChargeTypeThresholdExceeded
                            || f === PayrollFlags.BreakThresholdExceeded
                    ));

                    return matches.length > 0;
                },
            };

            // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
            // One column must have "checkboxSelection" set to true.
            // Note: headerCheckboxSelection not available with infinite scroll.
            gridOptions.rowSelection = 'multiple';

            gridOptions.postProcessData = self.transformRowData;
            gridOptions.onRowSelected = self.onRowSelected;
            gridOptions.columnDefs = self.getColumnDefs(
                hasFullView,
                hasEdit,
                hasIndividual,
            );

            // On the grid's first load, we want to apply the filters immediately from the state population,
            // before the API call is made.
            gridOptions.onGridReady = (params) => {
                // Calc past 2 weeks
                const start = moment(moment().subtract(14, 'days')).format(
                    'YYYY-MM-DD',
                );
                const end = moment(moment().subtract(1, 'days')).format(
                    'YYYY-MM-DD',
                );

                const filterInstance = gridOptions.api.getFilterInstance(
                    'Payroll.TimesheetDetail.Timesheet.JobDate',
                );
                filterInstance.setModel({
                    type: 'inRange',
                    dateFrom: start,
                    dateTo: end,
                });

                gridOptions.api.onFilterChanged();

                params.api.setDatasource(dataSource);
                self.setState({ gridApi: params.api });
            };

            // 2022-09-28 - M. Nicol - If the user does not have full access, (s)he can only see his/her own data.
            const searchUrl = hasIndividual
                ? ApiRoutes.payroll.searchindividual()
                : !hasEdit && hasFullView
                    ? ApiRoutes.payroll.searchfullview()
                    : ApiRoutes.payroll.search();

            const dataSource = createDataSource(searchUrl, gridOptions);

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                canViewBillingReport,
                // selectedMultiDispatchLocations: dispatchLocations
            });
        });
    };

    printTimesheet = async (id, hasFull) => {
        if (id) {
            const url = hasFull
                ? ApiRoutes.report.timesheetPayroll(id)
                : ApiRoutes.report.timesheetPayrollIndividual(id);

            window.open(url, '_self');
        }
    };

    // Load employees for the Reject modal filtered by the chosen dispatching.
    // Uses timesheet.slideoutStaff(dispatchLocationId) — returns staff/employees
    // assigned to that dispatch location.
    loadRejectEmployees = async (dispatchLocationId) => {
        if (!dispatchLocationId) {
            this.setState({ rejectEmployees: [], rejectSelectedEmployee: null });
            return;
        }

        this.setState({ isLoadingRejectEmployees: true });

        try {
            const entities = await util.fetch.js(
                ApiRoutes.timesheet.slideoutStaff(dispatchLocationId),
            );
            const rejectEmployees = (entities ?? []).map((e) => ({
                ...e,
                value: e.id,
                label: e.name ?? e.fullName ?? `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
            }));
            this.setState({
                rejectEmployees,
                rejectSelectedEmployee: null,
            });
        } catch (error) {
            console.error('Error loading reject employees:', error);
            this.setState({ rejectEmployees: [], rejectSelectedEmployee: null });
        } finally {
            this.setState({ isLoadingRejectEmployees: false });
        }
    };

    onRejectDispatchLocationChanged = async (selection) => {
        const value = selection ? selection.value : null;
        this.setState({ rejectDispatchLocation: value });
        await this.loadRejectEmployees(value);
    };

    onRejectEmployeeChanged = (selection) => {
        this.setState({ rejectSelectedEmployee: selection });
    };

    onRejectDestinationChanged = (selection) => {
        const value = selection.value;
        if (value === 'payroll') {
            this.setState({
                rejectDestination: value,
                rejectMode: 'week',
                rejectPayrollStatus: 'processed',
                rejectDate: '',
            });
        } else {
            this.setState({
                rejectDestination: value,
                rejectMode: 'day',
                rejectPayrollStatus: 'all',
                rejectDate: '',
            });
        }
    };

    onRejectModeChanged = (mode) => {
        this.setState({
            rejectMode: mode,
            rejectDate: '',
            rejectPayrollStatus: 'all',
        });
    };

    onRejectPayrollStatusChanged = (selection) => {
        this.setState({ rejectPayrollStatus: selection.value });
    };

    onRejectDateChanged = (e) => {
        const rawValue = e.currentTarget.value;
        const { rejectMode } = this.state;

        if (!rawValue) {
            this.setState({ rejectDate: '' });
            return;
        }

        // For "By week" mode, snap any non-Sunday pick back to the prior Sunday.
        // Parse the YYYY-MM-DD string manually to avoid UTC offset shifting the day.
        if (rejectMode === 'week') {
            const [y, m, d] = rawValue.split('-').map(Number);
            const picked = new Date(y, m - 1, d);

            if (picked.getDay() !== 0) {
                picked.setDate(picked.getDate() - picked.getDay()); // back to Sunday
                const snapped = `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(picked.getDate()).padStart(2, '0')}`;
                toasty.success(`Week of must start on a Sunday. Snapped to ${snapped}.`);
                this.setState({ rejectDate: snapped });
                return;
            }
        }

        this.setState({ rejectDate: rawValue });
    };

    rejectPayrollTimesheet = async () => {
        // Synchronous guard against double-click (setState is async, this isn't)
        if (this.isRejecting) return;

        const {
            rejectSelectedEmployee,
            rejectDestination,
            rejectMode,
            rejectDate,
            rejectPayrollStatus,
        } = this.state;

        if (!rejectSelectedEmployee || !rejectDate) {
            toasty.error('Please complete all required fields.');
            return;
        }

        // Build list of endpoints to call based on destination, mode + status:
        //   Reject To Payroll Management:
        //     - Always week + Processed -> reset rows to Pending in Payroll Management
        //   Reject To Ticket Management:
        //     - By day:                always Pending
        //     - By week + Pending:     Pending
        //     - By week + Processed:   Processed
        //     - By week + All:         BOTH Pending and Processed
        const endpointsToCall = [];
        if (rejectDestination === 'payroll') {
            endpointsToCall.push({
                url: ApiRoutes.payroll.rejectEmployeeProcessedToPayroll(),
                label: 'Payroll',
            });
        } else if (rejectMode === 'day') {
            endpointsToCall.push({
                url: ApiRoutes.payroll.rejectEmployeePending(),
                label: 'Pending',
            });
        } else if (rejectPayrollStatus === 'pending') {
            endpointsToCall.push({
                url: ApiRoutes.payroll.rejectEmployeePending(),
                label: 'Pending',
            });
        } else if (rejectPayrollStatus === 'processed') {
            endpointsToCall.push({
                url: ApiRoutes.payroll.rejectEmployeeProcessed(),
                label: 'Processed',
            });
        } else {
            // 'all' — hit both
            endpointsToCall.push(
                {
                    url: ApiRoutes.payroll.rejectEmployeePending(),
                    label: 'Pending',
                },
                {
                    url: ApiRoutes.payroll.rejectEmployeeProcessed(),
                    label: 'Processed',
                },
            );
        }

        this.isRejecting = true;
        this.setState({ isRejectingPayrollTimesheet: true });

        const model = {
            employeeId: rejectSelectedEmployee.value,
            rejectBy: rejectMode, // 'day' or 'week'
            date: new Date(rejectDate),
        };

        const failures = [];
        const successes = [];

        try {
            // Sequential — if "all" is selected and Pending fails, we still try Processed.
            // Each failure is captured so we can report a combined result.
            // eslint-disable-next-line no-restricted-syntax
            for (const ep of endpointsToCall) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const response = await util.fetch.post(ep.url, model);
                    if (response) {
                        // Server returned an error string in the body
                        failures.push(`${ep.label}: ${response}`);
                    } else {
                        successes.push(ep.label);
                    }
                } catch (error) {
                    failures.push(`${ep.label}: request failed`);
                    console.error(`Reject ${ep.label} error:`, error);
                }
            }

            if (failures.length === 0) {
                toasty.success(
                    successes.length > 1
                        ? `Charges rejected (${successes.join(', ')})`
                        : 'Charges rejected',
                );
            } else if (successes.length > 0) {
                // Partial success
                toasty.error(
                    `Partial reject — succeeded: ${successes.join(', ')}; failed: ${failures.join('; ')}`,
                );
            } else {
                toasty.error(`Error rejecting charges — ${failures.join('; ')}`);
            }
        } finally {
            this.isRejecting = false;
        }

        this.state.gridOptions.refresh();

        this.setState({
            selectedRow: null,
            rowsSelected: [],
            showRejectPayrollTimesheetModal: false,
            rejectSelectedEmployee: null,
            rejectDestination: 'ticket',
            rejectMode: 'day',
            rejectDate: '',
            rejectPayrollStatus: 'all',
            rejectDispatchLocation: null,
            rejectEmployees: [],
            isRejectingPayrollTimesheet: false,
        });
    };

    toggleDownloadModal = async (id) => {
        const { showDownloadModal } = this.state;
        this.setState({
            selectedExportId: id,
            showDownloadModal: !showDownloadModal,
        });
    };

    async toggleExportModal(weekly, billableOnly, isHourly) {
        const { showExportModal } = this.state;
        this.setState({
            showExportModal: !showExportModal,
            selectedDispatchLocation: null,
            selectedMultiDispatchLocations: null,
            selectedPayrollCompany: null,
            selectedWeekOf: '',
            isWeekly: !!weekly,
            isHourly: !!isHourly,
            exportBillableOnly: !!billableOnly,
        });
    }

    toggleRecalcModal = async () => {
        const { showRecalcModal } = this.state;
        this.setState({
            showRecalcModal: !showRecalcModal,
            selectedEmployee: 0,
        });
    };

    toggleRejectPayrollTimesheetModal = async () => {
        const { showRejectPayrollTimesheetModal, dispatchLocations } = this.state;

        const opening = !showRejectPayrollTimesheetModal;

        if (opening) {
            // Default the dispatching to "Cardinal Aggregates" (case-insensitive match).
            const defaultDispatch = (dispatchLocations ?? []).find(
                (d) => (d.label ?? '').trim().toLowerCase() === 'cardinal aggregates',
            );
            const defaultDispatchValue = defaultDispatch ? defaultDispatch.value : null;

            this.setState({
                showRejectPayrollTimesheetModal: true,
                rejectSelectedEmployee: null,
                rejectDestination: 'ticket',
                rejectMode: 'day',
                rejectDate: '',
                rejectPayrollStatus: 'all',
                rejectDispatchLocation: defaultDispatchValue,
                rejectEmployees: [],
            });

            if (defaultDispatchValue) {
                await this.loadRejectEmployees(defaultDispatchValue);
            }
        } else {
            this.setState({ showRejectPayrollTimesheetModal: false });
        }
    };

    transformRowData(data) {
        data.rows.forEach((row) => {
            // Combine these columns to save space.
            row.quantityDisplay = `${row.quantity} ${row.unitsName}`;

            // Placeholder column so we can have it be the last checkable thing on the right.
            // Otherwise, we could set checkboxSelection = true for an existing column.
            row.selectionPlaceholder = '';
        });
        return { ...data };
    }

    async toggleProcessModal() {
        const { showProcessModal } = this.state;
        this.setState({
            showProcessModal: !showProcessModal,
            selectedDispatchLocation: null,
            selectedWeekOf: '',
        });
    }

    onProcessWeekOfChanged = (e) => {
        const rawValue = e.currentTarget.value;

        if (!rawValue) {
            this.setState({ selectedWeekOf: '' });
            return;
        }

        // Snap any non-Sunday pick back to the prior Sunday.
        // Parse YYYY-MM-DD manually so timezone doesn't shift the day.
        const [y, m, d] = rawValue.split('-').map(Number);
        const picked = new Date(y, m - 1, d);

        if (picked.getDay() !== 0) {
            picked.setDate(picked.getDate() - picked.getDay()); // back to Sunday
            const snapped = `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(picked.getDate()).padStart(2, '0')}`;
            toasty.success(`Week of must start on a Sunday. Snapped to ${snapped}.`);
            this.setState({ selectedWeekOf: snapped });
            return;
        }

        this.setState({ selectedWeekOf: rawValue });
    };

    async onProcessClick() {
        const { selectedWeekOf, selectedDispatchLocation } = this.state;

        if (!selectedWeekOf || !selectedDispatchLocation) return;

        this.setState({ isProcessing: true });

        const model = {
            weekOf: new Date(selectedWeekOf),
            dispatchLocationId: selectedDispatchLocation,
        };

        try {
            const response = await util.fetch.post(ApiRoutes.payrollManagement.process(), model);
            if (response) {
                toasty.error(response);
            } else {
                toasty.success('Processing complete');
            }
        } catch {
            toasty.error('Error processing payroll records');
        }

        this.state.gridOptions.refresh();

        this.setState({ rowsSelected: [], isProcessing: false, showProcessModal: false });
    }

    render() {
        const {
            rowData,
            gridOptions,
            employees,
            dispatchLocations,
            payrollCompanies,
            selectedPayrollCompany,
            selectedDispatchLocation,
            selectedMultiDispatchLocations,
            selectedWeekOf,
            selectedEmployee,
            isExporting,
            showExportModal,
            showDownloadModal,
            showRecalcModal,
            isDownloading,
            isRecalculating,
            isWeekly,
            isHourly,
            exportBillableOnly,
            hasEdit,
            isRejectingPayrollTimesheet,
            showRejectPayrollTimesheetModal,
            showProcessModal,
            isProcessing,
            rejectSelectedEmployee,
            rejectDestination,
            rejectMode,
            rejectDate,
            rejectEmployees,
            rejectPayrollStatus,
            rejectDispatchLocation,
            isLoadingRejectEmployees,
        } = this.state;

        return (
            <CommonContext.Consumer>
                {(value) => {
                    const { tenantSettings } = (value ?? {}).tenant ?? {};

                    if (this.state.loading || !tenantSettings) {
                        return (
                            <LinearProgress
                                variant="indeterminate"
                                color="secondary"
                            />
                        );
                    }

                    if (!tenantSettings.payrollEnabled) return <NotAuthorizedPage />;

                    return (
                        <PageWrap>
                            <PageHeading>
                                <FontAwesomeIcon
                                    icon={faTasks}
                                    className="mr-2 text-muted"
                                />
                                <span>Payroll Management</span>

                                {hasEdit && (
                                    <>
                                        <span
                                            style={{
                                                float: 'right',
                                                position: 'relative',
                                                top: '-5px',
                                            }}
                                        >
                                            <Button
                                                size="sm"
                                                style={{ marginRight: '10px' }}
                                                color="success"
                                                onClick={() => this.toggleProcessModal()}
                                            >
                                                Process
                                            </Button>
                                            <Button
                                                size="sm"
                                                style={{ marginRight: '10px' }}
                                                color="primary"
                                                onClick={() => this.toggleExportModal()}
                                            >
                      Export
                                            </Button>
                                        </span>

                                        <span
                                            style={{
                                                float: 'right',
                                                position: 'relative',
                                                top: '-5px',
                                            }}
                                        >
                                            <Button
                                                size="sm"
                                                color="danger"
                                                style={{ marginRight: '10px' }}
                                                onClick={() => this.toggleRejectPayrollTimesheetModal()}
                                            >
                                                Reject
                                            </Button>
                                        </span>
                                    </>
                                )}
                            </PageHeading>

                            <DataGridToolbar
                                entity="Payroll"
                                gridApi={this.state.gridApi}
                                dataSource={this.state.dataSource}
                                hideAdd
                                hideExcelButton
                                gridOptions={this.state.gridOptions}
                                serverExport={{
                                    apiPath: this.getExcelAPIPath(hasEdit),
                                    filePrefix: 'PayrollCategoriesSearch',
                                }}
                                
                            />

                            <DataGrid
                                domLayout="normal"
                                rowData={rowData}
                                gridOptions={gridOptions}
                                gridStatus={this.state.gridStatus}
                            />
                            <TimesheetDetails
                                ref={this.timesheetDetailsRef}
                                show={this.state.timesheetDetailsOpen}
                                toggleShow={(open) => this.setState({
                                    timesheetDetailsOpen: open,
                                })}
                                onClose={this.onTimesheetDetailsClosed}
                                payroll
                                isPayroll
                                isBilling={false}
                                isTimesheet // janky way to show just reaodnly part of the status of the emergency
                                isTimesheetForm
                            />
                            <PayrollCalculationSlideout
                                ref={this.payrollCalculationRef}
                                show={this.state.payrollCalculationOpen}
                                toggleShow={(open) => this.setState({
                                    payrollCalculationOpen: open,
                                })}
                                onClose={this.onPayrollCalculationClosed}
                            />

                            <Modal
                                isOpen={showExportModal}
                                toggle={() => this.toggleExportModal()}
                            >
                                <ModalHeader
                                    toggle={() => this.toggleExportModal()}
                                >
                                    {!isWeekly
                                        && !exportBillableOnly
                                        && !isHourly && (
                                        <span>Export Payroll</span>
                                    )}
                                    {!!isHourly && <span>Hours Report</span>}
                                    {!!isWeekly && !exportBillableOnly && (
                                        <span>Export Payroll - All Time</span>
                                    )}
                                    {!!isWeekly && !!exportBillableOnly && (
                                        <span>Export Payroll - Billing</span>
                                    )}
                                </ModalHeader>
                                <ModalBody>
                                    {!isWeekly && !isHourly && (
                                        <FormGroup>
                                            <label>Operating Company</label>
                                            <Select
                                                placeholder="Select Operating Company"
                                                id="payrollCompanies"
                                                name="payrollCompanies"
                                                styles={CompactSelectStyles}
                                                isClearable={false}
                                                className="react-select"
                                                options={payrollCompanies}
                                                value={
                                                    (
                                                        payrollCompanies ?? []
                                                    ).find(
                                                        (x) => x.value
                                                            === selectedPayrollCompany,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this
                                                        .onSelectedPayrollCompanyChanged
                                                }
                                            />
                                        </FormGroup>
                                    )}
                                    {!isWeekly && !isHourly && (
                                        <FormGroup style={{ marginBottom: 0 }}>
                                            <label>Dispatching</label>
                                            <Select
                                                placeholder="Select Dispatching"
                                                id="selectedDispatchLocation"
                                                name="selectedDispatchLocation"
                                                styles={CompactSelectStyles}
                                                isClearable
                                                isMulti
                                                className="react-select"
                                                options={dispatchLocations}
                                                value={
                                                    selectedMultiDispatchLocations
                                                }
                                                onChange={
                                                    this
                                                        .onSelectedMultiDispatchLocationChanged
                                                }
                                            />
                                            <div>
                                                <small
                                                    className="float-right site-link"
                                                    onClick={() => this.onSelectedMultiDispatchLocationChanged(
                                                        this.state
                                                            .dispatchLocations,
                                                    )}
                                                >
                        Select All
                                                </small>
                                            </div>
                                        </FormGroup>
                                    )}
                                    {!!isWeekly && (
                                        <FormGroup>
                                            <label>Dispatching</label>
                                            <Select
                                                placeholder="Select Dispatching"
                                                id="selectedDispatchLocation"
                                                name="selectedDispatchLocation"
                                                styles={CompactSelectStyles}
                                                isClearable={false}
                                                className="react-select"
                                                options={dispatchLocations}
                                                value={
                                                    (
                                                        dispatchLocations ?? []
                                                    ).find(
                                                        (x) => x.value
                                                            === selectedDispatchLocation,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this
                                                        .onSelectedDispatchLocationChanged
                                                }
                                            />
                                        </FormGroup>
                                    )}
                                    <FormGroup>
                                        <label>Week Of</label>
                                        <input
                                            id="weekOf"
                                            name="selectedWeekOf"
                                            className="form-control"
                                            defaultValue={selectedWeekOf ?? ''}
                                            onChange={this.onChange}
                                            type="date"
                                        />
                                    </FormGroup>
                                </ModalBody>
                                <ModalFooter>
                                    {isExporting && (
                                        <FontAwesomeIcon
                                            icon={faCircleNotch}
                                            className="fa-spin mr-2"
                                            size="sm"
                                        />
                                    )}
                                    <Button
                                        color="primary"
                                        disabled={isExporting}
                                        onClick={() => (isWeekly
                                            ? this.onWeeklyExportClick()
                                            : isHourly
                                                ? this.onHourlyExportClick()
                                                : this.onExportClick())}
                                    >
                    Ok
                                    </Button>
                                    {' '}
                                    <Button
                                        color="secondary"
                                        onClick={() => this.setState({
                                            showExportModal: false,
                                        })}
                                    >
                    Cancel
                                    </Button>
                                </ModalFooter>
                            </Modal>

                            <Modal
                                isOpen={showDownloadModal}
                                toggle={() => this.toggleDownloadModal()}
                            >
                                <ModalHeader
                                    toggle={() => this.toggleDownloadModal()}
                                >
                  Download Payroll
                                </ModalHeader>
                                <ModalBody>
                                    <FlexCenterRow>
                                        <Button
                                            color="success"
                                            style={{ marginRight: '10px' }}
                                            disabled={isDownloading}
                                            onClick={() => this.onDownloadExportClick()}
                                        >
                      Export
                                        </Button>
                                        {' '}
                                        {/* <Button color="warning" disabled={isDownloading} onClick={() => this.onDownloadWeeklyClick()}>Weekly Employee</Button>{' '} */}
                                    </FlexCenterRow>
                                </ModalBody>
                                <ModalFooter>
                                    {isDownloading && (
                                        <FontAwesomeIcon
                                            icon={faCircleNotch}
                                            className="fa-spin mr-2"
                                            size="sm"
                                        />
                                    )}
                                    <Button
                                        color="secondary"
                                        onClick={() => this.setState({
                                            showDownloadModal: false,
                                        })}
                                    >
                    Cancel
                                    </Button>
                                </ModalFooter>
                            </Modal>

                            <Modal
                                isOpen={showRejectPayrollTimesheetModal}
                                toggle={this.toggleRejectPayrollTimesheetModal}
                            >
                                <ModalHeader
                                    toggle={
                                        this.toggleRejectPayrollTimesheetModal
                                    }
                                >
                                    Reject charges
                                </ModalHeader>
                                <ModalBody>
                                    <FormGroup>
                                        <label>Reject To</label>
                                        {(() => {
                                            const rejectDestinationOptions = [
                                                { value: 'ticket', label: 'Ticket Management' },
                                                { value: 'payroll', label: 'Payroll Management' },
                                            ];
                                            return (
                                                <Select
                                                    placeholder="Select destination"
                                                    id="rejectDestination"
                                                    name="rejectDestination"
                                                    styles={CompactSelectStyles}
                                                    isClearable={false}
                                                    className="react-select"
                                                    options={rejectDestinationOptions}
                                                    value={
                                                        rejectDestinationOptions.find(
                                                            (x) => x.value === rejectDestination,
                                                        ) ?? rejectDestinationOptions[0]
                                                    }
                                                    onChange={this.onRejectDestinationChanged}
                                                />
                                            );
                                        })()}
                                    </FormGroup>

                                    <FormGroup>
                                        <label>Dispatching</label>
                                        <Select
                                            placeholder="Select Dispatching"
                                            id="rejectDispatchLocation"
                                            name="rejectDispatchLocation"
                                            styles={CompactSelectStyles}
                                            isClearable={false}
                                            className="react-select"
                                            options={dispatchLocations}
                                            value={
                                                (dispatchLocations ?? []).find(
                                                    (x) => x.value === rejectDispatchLocation,
                                                ) ?? ''
                                            }
                                            onChange={this.onRejectDispatchLocationChanged}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <label>Employee</label>
                                        <Select
                                            placeholder={
                                                !rejectDispatchLocation
                                                    ? 'Select a dispatching first'
                                                    : isLoadingRejectEmployees
                                                        ? 'Loading employees...'
                                                        : 'Select an employee'
                                            }
                                            id="rejectSelectedEmployee"
                                            name="rejectSelectedEmployee"
                                            styles={CompactSelectStyles}
                                            isClearable={false}
                                            isDisabled={!rejectDispatchLocation || isLoadingRejectEmployees}
                                            isLoading={isLoadingRejectEmployees}
                                            className="react-select"
                                            options={rejectEmployees}
                                            value={rejectSelectedEmployee ?? ''}
                                            onChange={this.onRejectEmployeeChanged}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <label>Reject by</label>
                                        <div>
                                            <div
                                                className="form-check form-check-inline"
                                                style={{ marginRight: '20px' }}
                                            >
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="rejectMode"
                                                    id="rejectModeDay"
                                                    value="day"
                                                    checked={rejectMode === 'day'}
                                                    disabled={rejectDestination === 'payroll'}
                                                    onChange={() => this.onRejectModeChanged('day')}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="rejectModeDay"
                                                >
                                                    By day
                                                </label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="rejectMode"
                                                    id="rejectModeWeek"
                                                    value="week"
                                                    checked={rejectMode === 'week'}
                                                    disabled={rejectDestination === 'payroll'}
                                                    onChange={() => this.onRejectModeChanged('week')}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="rejectModeWeek"
                                                >
                                                    By week
                                                </label>
                                            </div>
                                        </div>
                                    </FormGroup>

                                    <FormGroup>
                                        <label>Payroll status</label>
                                        {rejectMode === 'week' && rejectDestination !== 'payroll' ? (
                                            (() => {
                                                const payrollStatusOptions = [
                                                    { value: 'all', label: 'All' },
                                                    { value: 'pending', label: 'Pending only' },
                                                    { value: 'processed', label: 'Processed only' },
                                                ];
                                                return (
                                                    <Select
                                                        placeholder="Select status"
                                                        id="rejectPayrollStatus"
                                                        name="rejectPayrollStatus"
                                                        styles={CompactSelectStyles}
                                                        isClearable={false}
                                                        className="react-select"
                                                        options={payrollStatusOptions}
                                                        value={
                                                            payrollStatusOptions.find(
                                                                (x) => x.value === rejectPayrollStatus,
                                                            ) ?? payrollStatusOptions[0]
                                                        }
                                                        onChange={this.onRejectPayrollStatusChanged}
                                                    />
                                                );
                                            })()
                                        ) : (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={
                                                    rejectDestination === 'payroll'
                                                        ? 'Processed only'
                                                        : 'Pending only'
                                                }
                                                disabled
                                                readOnly
                                            />
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <label>
                                            {rejectMode === 'week' ? 'Week of' : 'Date'}
                                        </label>
                                        <input
                                            id="rejectDate"
                                            name="rejectDate"
                                            className="form-control"
                                            placeholder="MM/DD/YYYY"
                                            value={rejectDate ?? ''}
                                            onChange={this.onRejectDateChanged}
                                            type="date"
                                        />
                                    </FormGroup>

                                    <div
                                        className="alert alert-info"
                                        style={{ marginTop: '0.5rem', marginBottom: 0 }}
                                    >
                                        {(() => {
                                            const destinationLabel = rejectDestination === 'payroll'
                                                ? 'Payroll Management'
                                                : 'Ticket Management';
                                            const employeeLabel = rejectSelectedEmployee
                                                ? rejectSelectedEmployee.label
                                                : 'the selected employee';
                                            const statusLabel = rejectDestination === 'payroll'
                                                ? 'processed'
                                                : rejectMode === 'day'
                                                    ? 'pending'
                                                    : rejectPayrollStatus === 'pending'
                                                        ? 'pending'
                                                        : rejectPayrollStatus === 'processed'
                                                            ? 'processed'
                                                            : 'pending and processed';
                                            const periodLabel = rejectMode === 'week'
                                                ? (rejectDate
                                                    ? `the week of ${moment(rejectDate).format('MM/DD/YYYY')}`
                                                    : 'the selected week')
                                                : (rejectDate
                                                    ? moment(rejectDate).format('MM/DD/YYYY')
                                                    : 'the selected day');
                                            const resultStatusLabel = rejectDestination === 'payroll'
                                                ? 'pending'
                                                : 'submitted';

                                            return (
                                                <span>
                                                    {`This will reject ${statusLabel} charges for `}
                                                    <strong>{employeeLabel}</strong>
                                                    {` for ${periodLabel} and send them back to `}
                                                    <strong>{destinationLabel}</strong>
                                                    {` in a ${resultStatusLabel} status.`}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </ModalBody>
                                <ModalFooter>
                                    {isRejectingPayrollTimesheet && (
                                        <FontAwesomeIcon
                                            icon={faCircleNotch}
                                            className="fa-spin mr-2"
                                            size="sm"
                                        />
                                    )}
                                    <Button
                                        color="secondary"
                                        disabled={isRejectingPayrollTimesheet}
                                        onClick={() => this.setState({
                                            showRejectPayrollTimesheetModal: false,
                                        })}
                                    >
                                        Cancel
                                    </Button>
                                    {' '}
                                    <Button
                                        color="danger"
                                        disabled={
                                            isRejectingPayrollTimesheet
                                            || !rejectSelectedEmployee
                                            || !rejectDate
                                        }
                                        onClick={this.rejectPayrollTimesheet}
                                    >
                                        Reject
                                    </Button>
                                </ModalFooter>
                            </Modal>

                            <Modal isOpen={showProcessModal} toggle={() => this.toggleProcessModal()}>
                                <ModalHeader toggle={() => this.toggleProcessModal()}>Process Payroll</ModalHeader>
                                <ModalBody>
                                    <FormGroup>
                                        <label>Dispatching</label>
                                        <Select
                                            placeholder="Select Dispatching"
                                            id="selectedDispatchLocation"
                                            name="selectedDispatchLocation"
                                            styles={CompactSelectStyles}
                                            isClearable={false}
                                            className="react-select"
                                            options={dispatchLocations}
                                            value={(dispatchLocations ?? []).find((x) => x.value === selectedDispatchLocation) ?? ''}
                                            onChange={this.onSelectedDispatchLocationChanged}
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <label>Week Of</label>
                                        <input
                                            id="weekOf"
                                            name="selectedWeekOf"
                                            className="form-control"
                                            value={selectedWeekOf ?? ''}
                                            onChange={this.onProcessWeekOfChanged}
                                            type="date"
                                        />
                                    </FormGroup>
                                </ModalBody>
                                <ModalFooter>
                                    {
                                        isProcessing
                                        && <FontAwesomeIcon icon={faCircleNotch} className="fa-spin mr-2" size="sm" />
                                    }
                                    <Button color="primary" disabled={isProcessing} onClick={() => this.onProcessClick()}>Ok</Button>
                                    {' '}
                                    <Button color="secondary" onClick={() => this.setState({ showProcessModal: false })}>Cancel</Button>
                                </ModalFooter>
                            </Modal>

                            <Modal
                                isOpen={showRecalcModal}
                                toggle={() => this.toggleRecalcModal()}
                                backdrop="static"
                                keyboard={false}
                            >
                                <ModalHeader>Recalculate Week</ModalHeader>
                                <ModalBody>
                                    <FormGroup>
                                        <label>Week Of</label>
                                        <input
                                            id="weekOf"
                                            name="selectedWeekOf"
                                            className="form-control"
                                            defaultValue={selectedWeekOf ?? ''}
                                            onChange={
                                                this
                                                    .onSelectedPayrollWeekChanged
                                            }
                                            type="date"
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <label>Dispatching</label>
                                        <Select
                                            placeholder="Select Dispatching"
                                            id="selectedDispatchLocation"
                                            name="selectedDispatchLocation"
                                            styles={CompactSelectStyles}
                                            isClearable={false}
                                            className="react-select"
                                            options={dispatchLocations}
                                            value={
                                                (dispatchLocations ?? []).find(
                                                    (x) => x.value
                                                        === selectedDispatchLocation,
                                                ) ?? ''
                                            }
                                            onChange={
                                                this
                                                    .onSelectedPayrollDispatchLocationChanged
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <label>Employee</label>
                                        <Select
                                            placeholder="Select Employee"
                                            id="selectedEmployee"
                                            name="selectedEmployee"
                                            styles={CompactSelectStyles}
                                            isClearable={false}
                                            options={employees}
                                            value={
                                                employees.find(
                                                    (i) => i.value
                                                        === selectedEmployee,
                                                ) ?? ''
                                            }
                                            onChange={
                                                this
                                                    .onSelectedPayrollEmployeeChanged
                                            }
                                        />
                                    </FormGroup>
                                    <Button
                                        color="success"
                                        style={{ marginRight: '10px' }}
                                        disabled={
                                            isRecalculating
                                            || selectedEmployee == null
                                            || selectedEmployee == 0
                                        }
                                        onClick={() => this.onRecalcClick()}
                                    >
                    Recalculate
                                    </Button>
                                    {' '}
                                    {isRecalculating && (
                                        <FontAwesomeIcon
                                            icon={faCircleNotch}
                                            className="fa-spin mr-2"
                                            size="sm"
                                        />
                                    )}
                                </ModalBody>
                                <ModalFooter>
                                    <Button
                                        color="secondary"
                                        disabled={isRecalculating}
                                        onClick={() => this.setState({
                                            showRecalcModal: false,
                                        })}
                                    >
                    Close
                                    </Button>
                                </ModalFooter>
                            </Modal>
                        </PageWrap>
                    );
                }}
            </CommonContext.Consumer>
        );
    }
}

export default withRouter(PayrollIndex);
